import { StringUtil } from '../utils';

interface IToken {
  name: string;
  value: TokenValue | (() => TokenValue);
}

type TokenValue = string | number;

const startSymbol = '{';
const endSymbol = '}';

/**
 * A class for dealing with string tokens to dynamically insert values.
 */
export class Tokenizer {
  
  /**
   * Replaces the given tokens inside the given string.
   * @param template                The string template to tokenize.
   * @param tokens                  The tokens to replace. If array of strings, index-based replacement is used. Otherwise the name of the token object is used.
   * @param replaceEmptyValues      Replace unmatched tokens with an empty string
   * 
   * @example
   * // 'A template with replaced tokens.'
   * Tokenizer.replaceTokens('A {prop} with replaced {name}.', [{ name: 'prop', value: 'template' }, { name: 'name', value: 'tokens' }]);
   * 
   * @example
   * // 'A template with index-based tokens.'
   * Tokenizer.replaceTokens('A {0} with {1} tokens.', ['template', 'index-based']);
   * 
   * @returns               The template with the resolved tokens.
   */
  static replaceTokens(template: string, tokens: TokenValue[] | IToken[], replaceEmptyValues: boolean = false): string {
    if (!tokens?.length) {
      return template;
    }

    let tokenizedValue = template;

    const convertIndexToToken = (tokenValue: TokenValue, index: number): IToken => {
      return {
        name: index.toString(),
        value: tokenValue,
      };
    };

    tokens.forEach((t: TokenValue | IToken, index: number) => {

      let token: IToken = typeof (t) === 'object' ? t : convertIndexToToken(t, index);

      const tokenValue = typeof(token.value) === 'function' ? token.value() : token.value;

      const replace = `\\${startSymbol}${token.name}\\${endSymbol}`;

      tokenizedValue = tokenizedValue.replace(new RegExp(replace, 'g'), StringUtil.safeValue(tokenValue));
    });    

    if (replaceEmptyValues){
      // Check if tokenized value has '{}' in it, because of null values and remove the brackets
      const regExp = "\{([^}]*)\}";
      if (tokenizedValue.match(regExp)){
        tokenizedValue = tokenizedValue.replace(/(\{.*?\})/g, '');
      }

      // Collapse all double whitespaces that may have been arose by removing empty brackets
      tokenizedValue = tokenizedValue.replace('  ', ' ');
    }

    return tokenizedValue;
  }
}